import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import { Button, Input, notification } from "antd";
// import {
//   CheckCircleOutlined,
//   InfoCircleOutlined,
// } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";

function normalizeAreaName(value) {
  return (value || "").trim();
}

function areaExists(list, name, ignoreIndex = -1) {
  const normalized = normalizeAreaName(name).toLowerCase();
  return list.some(
    (item, index) =>
      index !== ignoreIndex && item.toLowerCase() === normalized,
  );
}

const ManageAreasPopup = ({
  open,
  onClose,
  areas = [],
  onSave,
  saving = false,
}) => {
  const [draftAreas, setDraftAreas] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [newArea, setNewArea] = useState("");
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (open) {
      setDraftAreas([...areas]);
      setSearchText("");
      setNewArea("");
      setEditingIndex(-1);
      setEditValue("");
    }
  }, [open, areas]);

  const filteredAreas = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return draftAreas.map((name, index) => ({ name, index }));
    }
    return draftAreas
      .map((name, index) => ({ name, index }))
      .filter(({ name }) => name.toLowerCase().includes(query));
  }, [draftAreas, searchText]);

  const notifyError = (description) => {
    notification.open({
      message: "Error",
      description,
      placement: "topRight",
      // icon: <InfoCircleOutlined style={{ color: "red" }} />,
      duration: 2,
    });
  };

  const handleAdd = () => {
    const name = normalizeAreaName(newArea);
    if (!name) {
      notifyError("Area name cannot be empty.");
      return;
    }
    if (areaExists(draftAreas, name)) {
      notifyError("This area already exists.");
      return;
    }
    setDraftAreas((prev) => [...prev, name]);
    setNewArea("");
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditValue(draftAreas[index] || "");
  };

  const cancelEdit = () => {
    setEditingIndex(-1);
    setEditValue("");
  };

  const saveEdit = () => {
    const name = normalizeAreaName(editValue);
    if (!name) {
      notifyError("Area name cannot be empty.");
      return;
    }
    if (areaExists(draftAreas, name, editingIndex)) {
      notifyError("This area already exists.");
      return;
    }
    setDraftAreas((prev) =>
      prev.map((item, idx) => (idx === editingIndex ? name : item)),
    );
    cancelEdit();
  };

  const handleRemove = (index) => {
    setDraftAreas((prev) => prev.filter((_, idx) => idx !== index));
    if (editingIndex === index) {
      cancelEdit();
    } else if (editingIndex > index) {
      setEditingIndex((prev) => prev - 1);
    }
  };

  const handleSave = async () => {
    const uniqueSorted = [
      ...new Set(draftAreas.map((a) => normalizeAreaName(a)).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b));
    const ok = await onSave(uniqueSorted);
    if (ok) {
      notification.open({
        message: "Success",
        description: "Areas updated successfully.",
        placement: "topRight",
        // icon: <CheckCircleOutlined style={{ color: "green" }} />,
        duration: 2,
      });
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Manage areas</h5>
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={handleCancel}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimesCircle} size="lg" />
          </button>
        </div>

        <div className="row mb-3">
          <div className="col-12 mb-2">
            <label className="all_section_for_label_main_class">Search</label>
            <Input
              placeholder="Search areas..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </div>
          <div className="col-8">
            <label className="all_section_for_label_main_class">Add area</label>
            <Input
              placeholder="New area name"
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              onPressEnter={handleAdd}
            />
          </div>
          <div className="col-4 d-flex align-items-end">
            <Button type="primary" block onClick={handleAdd}>
              Add
            </Button>
          </div>
        </div>

        <div
          style={{
            maxHeight: "320px",
            overflowY: "auto",
            border: "1px solid #eee",
            borderRadius: "4px",
          }}
        >
          {filteredAreas.length === 0 ? (
            <p className="text-muted p-3 mb-0">No areas found.</p>
          ) : (
            <ul className="list-group list-group-flush">
              {filteredAreas.map(({ name, index }) => (
                <li
                  key={`${index}-${name}`}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  {editingIndex === index ? (
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onPressEnter={saveEdit}
                      style={{ flex: 1, marginRight: "8px" }}
                    />
                  ) : (
                    <span>{name}</span>
                  )}
                  <div className="btn-group btn-group-sm">
                    {editingIndex === index ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={saveEdit}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => startEdit(index)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => handleRemove(index)}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save changes
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

ManageAreasPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  areas: PropTypes.arrayOf(PropTypes.string),
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

export default ManageAreasPopup;
